import i18n from "i18next";
import { useTranslation } from "react-i18next";

import { Button } from "~/components/ui/button";
import { ArrowLeftDoubleIcon } from "~/components/ui/icons/arrow-left-double";
import { CheckIcon } from "~/components/ui/icons/check";
import { CopyIcon } from "~/components/ui/icons/copy";
import { ExternalLinkDefaultIcon } from "~/components/ui/icons/external-link-default";
import { ShopifyPluggedIcon } from "~/components/ui/icons/shopify-plugged";
import { Loader } from "~/components/ui/loader";
import { toast } from "~/components/ui/toast";
import { useApiKeyQuery } from "~/graphql/graphql.gen";
import { useCopyToClipboard } from "~/hooks/use-copy-to-clipboard";
import { useShopifyInstalledSubscription } from "~/hooks/use-shopify-installed-subscription";

import { Shopify } from "./shopify-icon";
import { ShopifyHeaderModal } from "./shopify-modal";

const SHOPIFY_APP_STORE_URL =
  import.meta.env.REACT_APP_STAGE === "production"
    ? `https://apps.shopify.com/choose-sync-plugin?locale=`
    : "https://admin.shopify.com/?organization_id=129462141&no_redirect=true&redirect=/oauth/redirect_from_developer_dashboard?client_id%3D8e6bcad4bc8642d6dbc07d5ed07227af&locale=";

export const ShopifyPluginInstall: React.FC<{
  onBack: () => void;
}> = ({ onBack }) => {
  const { t } = useTranslation();
  const { data: apiKeyData, isLoading: isApiKeyLoading } = useApiKeyQuery();
  const [copyState, copyToClipboard] = useCopyToClipboard();

  const apiKey = apiKeyData?.brand?.api_token ?? "";

  const handleCopy = () => {
    if (!apiKey) return;
    copyToClipboard(apiKey);
    toast.success(t("settings.integration_tabs.shopify.copied_to_clipboard"));
  };

  useShopifyInstalledSubscription();

  const appStoreUrl = `${SHOPIFY_APP_STORE_URL}${i18n.language}`;

  return (
    <div className="h-full overflow-y-auto">
      <div className="p-10">
        <div className="space-y-10">
          <ShopifyHeaderModal showRetroactiveAlert={false} />

          <Button type="button" variant="ghost" size="sm" onClick={onBack}>
            <ArrowLeftDoubleIcon className="size-4" />
            {t("settings.integration_tabs.shopify.back")}
          </Button>

          <div className="space-y-6">
            {/* Step 1: Install plugin */}
            <div className="rounded-lg border border-gray-200 p-6">
              <div className="flex items-start gap-4">
                <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-gray-100 text-sm font-semibold text-gray-900">
                  1
                </div>
                <div className="flex-1 space-y-2">
                  <h4 className="font-semibold">
                    {t("settings.integration_tabs.shopify.step_1_title")}
                  </h4>
                  <p className="text-sm text-gray-700">
                    {t("settings.integration_tabs.shopify.step_1_description")}
                  </p>
                  <a
                    href={appStoreUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center pt-2"
                  >
                    <Shopify fill="#96BF48" className="size-6" />
                    <p className="ml-2 flex-1 text-sm font-semibold text-green-900">
                      {t(
                        "settings.integration_tabs.shopify.open_shopify_app_store",
                      )}
                    </p>
                    <ExternalLinkDefaultIcon className="ml-1 size-3.5 text-green-900" />
                  </a>
                </div>
              </div>
            </div>

            {/* Step 2: Copy API key */}
            <div className="rounded-lg border border-gray-200 p-6">
              <div className="flex items-start gap-4">
                <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-gray-100 text-sm font-semibold text-gray-900">
                  2
                </div>
                <div className="flex-1 space-y-2">
                  <h4 className="font-semibold">
                    {t("settings.integration_tabs.shopify.step_2_title")}
                  </h4>
                  <p className="text-sm text-gray-700">
                    {t("settings.integration_tabs.shopify.step_2_description")}
                  </p>
                  {isApiKeyLoading ? (
                    <p className="text-sm text-gray-500">
                      {t("settings.integration_tabs.shopify.loading_api_key")}
                    </p>
                  ) : (
                    <div className="flex flex-1 items-center gap-2 rounded-lg border border-gray-200 py-1.5">
                      <code className="flex-1 px-3 text-sm">{apiKey}</code>
                      <Button
                        type="button"
                        variant="link"
                        size="sm"
                        onClick={handleCopy}
                      >
                        {copyState.value === apiKey ? (
                          <CheckIcon className="size-4" />
                        ) : (
                          <CopyIcon className="size-4" />
                        )}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Step 3: Waiting for connection */}
            <div className="rounded-lg border border-gray-200 p-6">
              <div className="flex items-start gap-4">
                <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-gray-100 text-sm font-semibold text-gray-900">
                  3
                </div>
                <div className="flex-1 space-y-2">
                  <h4 className="font-semibold">
                    {t("settings.integration_tabs.shopify.step_3_title")}
                  </h4>
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Loader className="size-4" />
                    {t("settings.integration_tabs.shopify.step_3_waiting")}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const ShopifyInstallChoice: React.FC<{
  onSelectPlugin: () => void;
  onSelectManual: () => void;
}> = ({ onSelectPlugin, onSelectManual }) => {
  const { t } = useTranslation();

  return (
    <div className="h-full overflow-y-auto">
      <div className="p-10">
        <div className="space-y-10">
          <ShopifyHeaderModal showRetroactiveAlert={false} />

          <h3 className="font-bold text-gray-900">
            {t("settings.integration_tabs.shopify.install_method_choice_title")}
          </h3>

          <div className="grid gap-4">
            <button
              type="button"
              onClick={onSelectPlugin}
              className="flex items-start gap-4 rounded-lg border border-gray-200 p-6 text-left transition-colors hover:border-gray-300 hover:bg-gray-50"
            >
              <ShopifyPluggedIcon
                title="Shopify"
                className="size-10 shrink-0"
              />
              <div>
                <p className="font-semibold">
                  {t("settings.integration_tabs.shopify.quick_install_title")}
                </p>
                <p className="mt-1 text-sm text-gray-700">
                  {t(
                    "settings.integration_tabs.shopify.quick_install_description",
                  )}
                </p>
              </div>
            </button>

            <button
              type="button"
              onClick={onSelectManual}
              className="flex items-start gap-4 rounded-lg border border-gray-200 p-6 text-left transition-colors hover:border-gray-300 hover:bg-gray-50"
            >
              <ExternalLinkDefaultIcon className="mt-1 size-6 shrink-0 text-gray-400" />
              <div>
                <p className="font-semibold">
                  {t("settings.integration_tabs.shopify.manual_setup_title")}
                </p>
                <p className="mt-1 text-sm text-gray-700">
                  {t(
                    "settings.integration_tabs.shopify.manual_setup_description",
                  )}
                </p>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
